import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const ACCENT = '#1E5FA8';
const CTA_LABEL = 'Ücretsiz başla';

function Tick() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
      <path d="M3 8.5l3.5 3L13 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LandingNav() {
  return (
    <header className="lp-nav">
      <div className="lp-nav-inner">
        <Link to="/landing" className="lp-brand">
          <div className="lp-brand-mark" style={{ background: ACCENT }}>X</div>
          <div className="lp-brand-text">
            <div className="lp-brand-name">OnxFlow</div>
            <div className="lp-brand-sub">Proje Yönetimi</div>
          </div>
        </Link>
        <nav className="lp-nav-links">
          <a href="#urun">Ürün</a>
          <Link to="/pricing">Fiyatlandırma</Link>
          <a href="#iletisim">İletişim</a>
        </nav>
        <div className="lp-nav-cta">
          <Link to="/login" className="lp-link-quiet">Giriş yap</Link>
          <Link to="/login" className="lp-btn-primary" style={{ background: ACCENT }}>{CTA_LABEL}</Link>
        </div>
      </div>
    </header>
  );
}

function FloatingChips({ progress }) {
  const opacity = Math.max(0, 1 - progress * 1.5);
  return (
    <>
      <div
        className="lp-chip lp-chip-task"
        style={{ opacity, animation: 'lpFloatIn 700ms 200ms both, lpFloatBob 6s ease-in-out infinite 700ms' }}
      >
        <div className="lp-chip-mark" style={{ background: ACCENT }}>T</div>
        <div className="lp-chip-body">
          <div className="lp-chip-eyebrow">Yeni görev</div>
          <div className="lp-chip-title">API hız limiti revizyonu</div>
        </div>
      </div>

      <div
        className="lp-chip lp-chip-pill"
        style={{ opacity, animation: 'lpFloatIn 700ms 400ms both, lpFloatBob 6s ease-in-out infinite 1.1s' }}
      >
        <span className="lp-dot lp-dot-emerald"></span>
        <span className="lp-chip-pill-text">Onaylandı · Sprint 24</span>
      </div>

      <div
        className="lp-chip lp-chip-progress"
        style={{ opacity, animation: 'lpFloatIn 700ms 600ms both, lpFloatBob 6s ease-in-out infinite 1.5s' }}
      >
        <div className="lp-chip-progress-row">
          <span className="lp-chip-eyebrow">Tamamlanma</span>
          <span className="lp-chip-progress-num">%72</span>
        </div>
        <div className="lp-chip-track">
          <div className="lp-chip-fill" style={{ width: '72%' }}></div>
        </div>
      </div>
    </>
  );
}

function Hero() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const heroH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const progress = Math.min(1, scrollY / (heroH * 0.6));
  const tilt = 14 * (1 - progress);
  const lift = -progress * 40;

  return (
    <section className="lp-hero">
      <div className="lp-hero-grid" />
      <div
        className="lp-hero-glow"
        style={{
          background: `radial-gradient(ellipse, color-mix(in oklch, ${ACCENT} 22%, transparent), transparent 60%)`,
        }}
      />

      <div className="lp-hero-inner">
        <div className="lp-eyebrow-pill">
          <span className="lp-dot" style={{ background: ACCENT, boxShadow: `0 0 6px ${ACCENT}` }}></span>
          <span>OnxFlow v2.0 · Türkiye</span>
        </div>

        <h1 className="lp-hero-h1">
          Ekibinizin{' '}
          <span
            className="lp-hero-italic"
            style={{
              background: `linear-gradient(90deg, ${ACCENT}, color-mix(in oklch, ${ACCENT} 55%, #6c4ad9))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            tek
          </span>{' '}
          proje
          <br />
          yönetim platformu.
        </h1>

        <p className="lp-hero-sub">
          Görev panosu, projeler, iş akışları, onaylar ve raporlama — dağınık araçların yerine, ekibinizin tek bir akış üzerinde
          çalıştığı kurumsal platform.
        </p>

        <div className="lp-hero-ctas">
          <Link
            to="/login"
            className="lp-btn-primary lp-btn-lg"
            style={{ background: ACCENT, boxShadow: `0 6px 20px -8px ${ACCENT}` }}
          >
            {CTA_LABEL}
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10m0 0L9 4m4 4l-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
          <Link to="/login" className="lp-btn-ghost lp-btn-lg">
            Ürünü inceleyin
          </Link>
        </div>

        <div className="lp-hero-meta">
          <span><Tick /> Kredi kartı gerektirmez</span>
          <span className="lp-hide-sm"><Tick /> Türkçe destek</span>
          <span className="lp-hide-sm"><Tick /> 14 gün ücretsiz</span>
        </div>
      </div>

      <div className="lp-hero-frame-wrap">
        <div
          className="lp-hero-frame"
          style={{
            transform: `rotateX(${tilt}deg) translateY(${lift}px)`,
          }}
        >
          <div className="lp-frame-bar">
            <span className="lp-frame-dot" style={{ background: '#fda4af' }}></span>
            <span className="lp-frame-dot" style={{ background: '#fcd34d' }}></span>
            <span className="lp-frame-dot" style={{ background: '#6ee7b7' }}></span>
            <div className="lp-frame-url">
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M11 7V5a3 3 0 10-6 0v2M4 7h8v6H4V7z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              app.onxflow.com/dashboard
            </div>
          </div>
          <img src="/screenshots/dashboard.png" alt="OnxFlow Dashboard" className="lp-frame-img" />
        </div>
        <FloatingChips progress={progress} />
      </div>
    </section>
  );
}

function TrustBand() {
  const stats = [
    { num: 'Kanban', sub: 'Liste · Zaman çizelgesi' },
    { num: 'RBAC', sub: 'Rol bazlı erişim' },
    { num: 'TR', sub: 'Yerinde destek' },
    { num: 'KVKK', sub: 'Uyumlu altyapı' },
  ];
  return (
    <section className="lp-trust">
      <div className="lp-trust-inner">
        <div className="lp-trust-eyebrow">— Ekiplerin tek araçta birleştirdiği temel işlevler —</div>
        <div className="lp-trust-grid">
          {stats.map((s, i) => (
            <div key={i} className="lp-trust-cell">
              <div className="lp-trust-num">{s.num}</div>
              <div className="lp-trust-sub">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureIcon({ kind }) {
  const props = {
    width: 18,
    height: 18,
    viewBox: '0 0 20 20',
    fill: 'none',
    strokeWidth: 1.6,
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  if (kind === 'board') return <svg {...props}><rect x="2.5" y="3" width="4" height="14" rx="1" /><rect x="8" y="3" width="4" height="9" rx="1" /><rect x="13.5" y="3" width="4" height="11" rx="1" /></svg>;
  if (kind === 'project') return <svg {...props}><path d="M3 5h6l1.5 2H17v9H3V5z" /><path d="M3 10h14" /></svg>;
  if (kind === 'workflow') return <svg {...props}><circle cx="4" cy="5" r="1.6" /><circle cx="16" cy="5" r="1.6" /><circle cx="4" cy="15" r="1.6" /><circle cx="16" cy="15" r="1.6" /><path d="M5.5 5h9M5.5 15h9M4 6.5v7M16 6.5v7" /></svg>;
  if (kind === 'approval') return <svg {...props}><path d="M3 5h14v10H3V5z" /><path d="M7 10l2 2 4-4" /></svg>;
  if (kind === 'report') return <svg {...props}><path d="M3 17V6m4 11V9m4 8v-5m4 5V4" /><path d="M2.5 17h15" /></svg>;
  if (kind === 'audit') return <svg {...props}><circle cx="10" cy="10" r="7" /><path d="M10 6v4l2.5 2" /></svg>;
  if (kind === 'team') return <svg {...props}><circle cx="7" cy="8" r="2.5" /><circle cx="14" cy="8" r="2.5" /><path d="M3 16c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5M11 16c0-1.5 1.3-3 3-3s3 1.5 3 3" /></svg>;
  if (kind === 'bell') return <svg {...props}><path d="M5 12V8a5 5 0 0110 0v4l1.5 2H3.5L5 12z" /><path d="M8.5 16.5a1.5 1.5 0 003 0" /></svg>;
  return null;
}

function FeatureGrid() {
  const features = [
    { icon: 'board', title: 'Görev Panosu', desc: 'Kanban, liste ve zaman çizelgesi görünümleri arasında geçiş yapın. Sürükle-bırak, öncelik, etiket ve atama tek panelde.' },
    { icon: 'project', title: 'Projeler & Modüller', desc: 'Her projeyi modüllere bölün. Görevleri modül bazında izleyin, ilerlemeyi tek görünümde takip edin.' },
    { icon: 'workflow', title: 'Özelleştirilebilir İş Akışları', desc: 'Durum geçişlerini, otomatik atamaları ve onay adımlarını ekibinize göre tanımlayın.' },
    { icon: 'approval', title: 'Onay Süreçleri', desc: 'İstekleri merkezi panelden yönetin. Çok adımlı onay zincirleri ve denetim izleri otomatik kayıt altında.' },
    { icon: 'report', title: 'Raporlama & KPI', desc: 'Tamamlanma, gecikme, ortalama süre — kurumsal raporları tek tıkla dışa aktarın.' },
    { icon: 'audit', title: 'Aktivite Günlüğü', desc: 'Her değişiklik kim, ne zaman, hangi alan — tam denetim izi ve geriye dönük arama.' },
    { icon: 'team', title: 'Takımlar & Roller', desc: 'Rol bazlı erişim matrisi ile kim hangi projeyi görür, kim onay verir — tek panelden yönetilir.' },
    { icon: 'bell', title: 'Bildirim Yönetimi', desc: 'E-posta ve uygulama içi bildirimleri kanal bazında ayarlayın. Geçmiş kayıtlardan teslim doğrulama.' },
  ];

  return (
    <section id="urun" className="lp-section lp-section-light">
      <div className="lp-container">
        <div className="lp-section-head">
          <div className="lp-mono-eyebrow">— Tek platform</div>
          <h2 className="lp-h2">
            Proje yönetimi için <span className="lp-h2-mute">ihtiyacınız olan her şey,</span> tek arayüzde.
          </h2>
          <p className="lp-section-sub">
            OnxFlow, ekiplerin görev takibinden onay süreçlerine, raporlamadan rol yönetimine kadar tüm proje yönetimi
            ihtiyaçlarını tek bir kurumsal platformda toplar.
          </p>
        </div>

        <div className="lp-feature-grid">
          {features.map((f, i) => (
            <div key={i} className="lp-feature-cell">
              <div
                className="lp-feature-icon"
                style={{
                  background: `color-mix(in oklch, ${ACCENT} 12%, white)`,
                  color: ACCENT,
                }}
              >
                <FeatureIcon kind={f.icon} />
              </div>
              <div className="lp-feature-title">{f.title}</div>
              <div className="lp-feature-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureBullet({ children }) {
  return (
    <li className="lp-bullet">
      <span className="lp-bullet-dot"></span>
      <span>{children}</span>
    </li>
  );
}

function TaskBoardShowcase() {
  return (
    <section className="lp-section lp-section-dark">
      <div className="lp-dark-grid" />
      <div className="lp-container lp-grid-12">
        <div className="lp-col-5">
          <div className="lp-mono-eyebrow lp-eyebrow-on-dark">— Görev Panosu</div>
          <h2 className="lp-h2 lp-h2-on-dark">
            Sürükleyin, bırakın,<br />
            <span className="lp-h2-mute-on-dark">akış devam etsin.</span>
          </h2>
          <p className="lp-section-sub lp-section-sub-on-dark">
            Beklemede, İşlemde, İncelemede ve Tamamlandı kolonları arasında görevleri sürükle-bırak ile taşıyın.
            Pano, liste veya zaman çizelgesi — ekibin tercih ettiği görünüme tek tıkla geçin.
          </p>
          <ul className="lp-bullets lp-bullets-on-dark">
            <FeatureBullet>Öncelik, etiket ve atama tek panelde</FeatureBullet>
            <FeatureBullet>Filtreler: proje, kullanıcı, öncelik, tarih</FeatureBullet>
            <FeatureBullet>Toplu işlemler ve dışa aktarma (CSV)</FeatureBullet>
          </ul>
        </div>
        <div className="lp-col-7">
          <div className="lp-shot-frame lp-shot-frame-on-dark">
            <img src="/screenshots/tasks.png" alt="Görev Panosu" className="lp-shot-img" />
          </div>
        </div>
      </div>
    </section>
  );
}

function WorkflowsShowcase() {
  return (
    <section id="akislar" className="lp-section lp-section-light">
      <div className="lp-container lp-grid-12">
        <div className="lp-col-7 lp-order-1">
          <div className="lp-shot-frame">
            <img src="/screenshots/workflows.png" alt="İş Akışları" className="lp-shot-img" />
          </div>
        </div>
        <div className="lp-col-5 lp-order-2">
          <div className="lp-mono-eyebrow">— İş Akışları</div>
          <h2 className="lp-h2">
            Süreçlerinizi <span style={{ color: ACCENT }}>kodsuz</span> tasarlayın.
          </h2>
          <p className="lp-section-sub">
            Durum geçişlerini, otomatik atamaları ve onay zincirlerini ekibinize özel tanımlayın.
            Standart şablonlarla başlayın, kendi akışlarınıza dönüştürün.
          </p>
          <ul className="lp-bullets">
            <FeatureBullet>Sürükle-bırak akış tasarımı</FeatureBullet>
            <FeatureBullet>Koşullu yönlendirme ve otomatik atama</FeatureBullet>
            <FeatureBullet>Şablon kütüphanesi ve sürüm geçmişi</FeatureBullet>
          </ul>
        </div>
      </div>
    </section>
  );
}

function ApprovalsShowcase() {
  return (
    <section className="lp-section lp-section-gray">
      <div className="lp-container lp-grid-12">
        <div className="lp-col-5">
          <div className="lp-mono-eyebrow">— Onaylar</div>
          <h2 className="lp-h2">
            Kararları <span className="lp-h2-mute">e-posta zincirinden</span> kurtarın.
          </h2>
          <p className="lp-section-sub">
            Onay isteklerini merkezi panelde toplayın. Çok adımlı zincirler, son tarih hatırlatmaları ve denetim izleri
            tek arayüzde — kim ne zaman neyi onayladı, eksiksiz görünür.
          </p>
          <ul className="lp-bullets">
            <FeatureBullet>Çok adımlı onay zincirleri</FeatureBullet>
            <FeatureBullet>Otomatik bildirim ve son tarih takibi</FeatureBullet>
            <FeatureBullet>KVKK uyumlu denetim izi</FeatureBullet>
          </ul>
        </div>
        <div className="lp-col-7">
          <div className="lp-shot-frame">
            <img src="/screenshots/approvals.png" alt="Onaylar" className="lp-shot-img" />
          </div>
        </div>
      </div>
    </section>
  );
}

function ReportsShowcase() {
  const metrics = [
    { label: 'Tamamlanma oranı', value: 'Trend' },
    { label: 'Ortalama süre', value: 'Karşılaştırma' },
    { label: 'Gecikme analizi', value: 'Otomatik' },
    { label: 'Atama dağılımı', value: 'CSV ihraç' },
  ];
  return (
    <section id="raporlar" className="lp-section lp-section-light">
      <div className="lp-container">
        <div className="lp-section-head lp-section-head-narrow">
          <div className="lp-mono-eyebrow">— Raporlama</div>
          <h2 className="lp-h2">
            Veriye dayalı kararlar,<br />
            <span className="lp-h2-mute">tek panodan.</span>
          </h2>
          <p className="lp-section-sub">
            Tamamlanma oranı, ortalama süre, gecikme analizi ve kullanıcı atama dağılımı — kurumsal raporlar
            tek tıkla CSV olarak dışa aktarılır.
          </p>
        </div>

        <div className="lp-browser-frame">
          <div className="lp-frame-bar">
            <span className="lp-frame-dot" style={{ background: '#fda4af' }}></span>
            <span className="lp-frame-dot" style={{ background: '#fcd34d' }}></span>
            <span className="lp-frame-dot" style={{ background: '#6ee7b7' }}></span>
            <div className="lp-frame-url">app.onxflow.com/reports</div>
          </div>
          <img src="/screenshots/reports.png" alt="Raporlar" className="lp-shot-img" />
        </div>

        <div className="lp-metric-grid">
          {metrics.map((m, i) => (
            <div key={i} className="lp-metric-card">
              <div className="lp-metric-label">{m.label}</div>
              <div className="lp-metric-value">{m.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SecurityCard({ title, desc }) {
  return (
    <div className="lp-sec-card">
      <div className="lp-sec-card-title">{title}</div>
      <div className="lp-sec-card-desc">{desc}</div>
    </div>
  );
}

function SecuritySection() {
  return (
    <section id="guvenlik" className="lp-section lp-section-dark">
      <div className="lp-dark-grid" />
      <div className="lp-container lp-sec-grid">
        <div>
          <div className="lp-mono-eyebrow lp-eyebrow-on-dark">— Güvenlik</div>
          <h2 className="lp-h2 lp-h2-on-dark">
            Kurumsal düzeyde<br />güvenlik ve yetki kontrolü.
          </h2>
          <p className="lp-section-sub lp-section-sub-on-dark">
            Rol bazlı erişim matrisi, denetim izleri ve KVKK uyumlu altyapı —
            hassas verilerinizi koruyan kurumsal kontroller standart olarak gelir.
          </p>
        </div>
        <div className="lp-sec-card-grid">
          <SecurityCard title="Rol Bazlı Erişim" desc="Granüler yetki matrisi, çoklu rol desteği" />
          <SecurityCard title="Aktivite Günlüğü" desc="Her eylem için tam denetim izi" />
          <SecurityCard title="Güvenlik Testi" desc="Yerleşik tarama ve sertleştirme önerileri" />
          <SecurityCard title="KVKK Uyumlu" desc="Veri lokasyonu Türkiye, şifrelenmiş depolama" />
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="lp-section lp-section-light lp-closing">
      <div
        className="lp-closing-glow"
        style={{
          background: `radial-gradient(ellipse, color-mix(in oklch, ${ACCENT} 18%, transparent), transparent 65%)`,
        }}
      />
      <div className="lp-container lp-closing-inner">
        <h2 className="lp-closing-h">
          Ekibinizle{' '}
          <span
            className="lp-hero-italic"
            style={{
              background: `linear-gradient(90deg, ${ACCENT}, color-mix(in oklch, ${ACCENT} 55%, #6c4ad9))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            bugün
          </span>{' '}
          başlayın.
        </h2>
        <p className="lp-closing-sub">Kurulum dakikalar içinde tamamlanır. Kredi kartı gerekmez.</p>
        <div className="lp-hero-ctas">
          <Link
            to="/login"
            className="lp-btn-primary lp-btn-lg"
            style={{ background: ACCENT, boxShadow: `0 6px 20px -8px ${ACCENT}` }}
          >
            {CTA_LABEL}
          </Link>
          <Link to="/login" className="lp-btn-ghost lp-btn-lg">
            Demo planlayın
          </Link>
        </div>
      </div>
    </section>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="lp-footer-col-title">{title}</div>
      <ul className="lp-footer-col-list">
        {items.map((it, i) => (
          <li key={i}>
            {it.to ? <Link to={it.to}>{it.l}</Link> : <a href={it.h || '#'}>{it.l}</a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LandingFooter() {
  return (
    <footer id="iletisim" className="lp-footer">
      <div className="lp-container">
        <div className="lp-footer-top">
          <div className="lp-footer-brand">
            <div className="lp-footer-brand-row">
              <div className="lp-brand-mark" style={{ background: ACCENT }}>X</div>
              <div className="lp-brand-text">
                <div className="lp-brand-name lp-text-white">OnxFlow</div>
                <div className="lp-brand-sub">Proje Yönetimi v2.0</div>
              </div>
            </div>
            <p className="lp-footer-blurb">
              Görev panosundan onaylara, raporlamadan rol yönetimine — ekipler için kurumsal proje platformu.
            </p>
          </div>
          <FooterCol
            title="Ürün"
            items={[
              { l: 'Görev Panosu', h: '#urun' },
              { l: 'İş Akışları', h: '#akislar' },
              { l: 'Raporlar', h: '#raporlar' },
              { l: 'Fiyatlandırma', to: '/pricing' },
            ]}
          />
          <FooterCol
            title="Şirket"
            items={[
              { l: 'Hakkımızda', h: '#' },
              { l: 'İletişim', h: '#iletisim' },
              { l: 'KVKK', h: '#' },
              { l: 'Şartlar', h: '#' },
            ]}
          />
          <FooterCol
            title="Destek"
            items={[
              { l: 'Yardım merkezi', h: '#' },
              { l: 'Sürüm notları', h: '#' },
              { l: 'Durum sayfası', h: '#' },
              { l: 'Geri bildirim', h: '#iletisim' },
            ]}
          />
        </div>
        <div className="lp-footer-bottom">
          <div className="lp-footer-copy">© 2026 OnxFlow · Tüm hakları saklıdır</div>
          <div className="lp-footer-status">
            <span className="lp-dot lp-dot-emerald"></span>
            <span>Tüm sistemler operasyonel</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const rootRef = useRef(null);

  useEffect(() => {
    document.title = 'OnxFlow — Kurumsal Proje Yönetim Platformu';
  }, []);

  return (
    <div ref={rootRef} className="lp-root">
      <LandingNav />
      <Hero />
      <TrustBand />
      <FeatureGrid />
      <TaskBoardShowcase />
      <WorkflowsShowcase />
      <ApprovalsShowcase />
      <ReportsShowcase />
      <SecuritySection />
      <ClosingCTA />
      <LandingFooter />
    </div>
  );
}
